import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Formik } from 'formik';
import { Modal, Button } from '@rsos/components/capstone/base';
import {
  ModalBody,
  ModalFooter,
} from '@rsos/components/capstone/base/Modal/ModalComponents';
import { notifySuccess } from '@rsos/utils/toastUtils';
import createIntegrationCredential from '../../actions/createIntegrationCredential';
import {
  GENERIC_REQUEST_ERROR_MESSAGE_PRIMARY,
  GENERIC_REQUEST_ERROR_MESSAGE_SECONDARY,
  CREDENTIAL_ERROR_MESSAGE,
} from '../../constants/errorMessages';
import { defaultSchema } from '../partnerSchema';
import { validationSchema } from '../validationSchema';
import { ButtonContainer } from './Modal.styles';
import DataSourcesForm from './components/DataSourcesForm';
import DataSourcesSelect from './components/DataSourcesSelect';
import { ErrorContainer } from './components/ErrorContainer';
import { ModalHeaders } from './components/ModalHeaders';
import { parsedHttp } from './parsedHttp';

const AddCredentialModal = ({
  onClose,
  isOpened,
  dataSource,
  psaps,
  orgID,
}) => {
  const [partner, setPartner] = useState(
    dataSource.length === 1 ? dataSource[0] : null,
  );
  const [hoveredPartner, setHoveredPartner] = useState(null);
  const [selected, setSelected] = useState(false);
  const [error, setError] = useState(null);
  const dispatch = useDispatch();

  const initialValues = Object.keys(defaultSchema).reduce((a, c) => {
    a[c] = '';
    return a;
  }, {});

  const handleCloseModal = () => {
    onClose();
    setPartner(dataSource.length === 1 ? dataSource[0] : null);
    setSelected(false);
    setError(false);
  };
  const checkBtnDisabled = values => {
    if (partner) {
      if (selected) {
        let hasInvalid = false;
        for (const [k, v] of Object.entries(values)) {
          if (v.trim() === '' && k !== 'device_search_subdomain') {
            hasInvalid = true;
          }
        }
        return hasInvalid;
      }
      return false;
    }
    return true;
  };

  const handleSubmit = async values => {
    if (!selected) {
      setSelected(true);
    } else {
      const authority_account_id = psaps?.currentPSAP?.account_id;
      const device_search_subdomain = values.device_search_subdomain
        ? `${parsedHttp(values.device_search_subdomain)}`
        : '';
      const normalizedCredential = {
        ...values,
        data_source: partner.name,
        authority_account_id,
        created_at: new Date(),
        device_search_subdomain,
      };

      // should remove device_search_subdomain key when it is empty:
      if (!device_search_subdomain) {
        delete normalizedCredential['device_search_subdomain'];
      }

      const response = await dispatch(
        createIntegrationCredential(orgID, normalizedCredential),
      );
      if (response.id) {
        onClose();
        notifySuccess('Credentials have been validated.');
        setError(null);
      } else {
        setError(response.status);
      }
    }
  };

  const handleChangeWithError = (onChange, e) => {
    if (error) {
      setError(null);
    }
    onChange(e);
  };

  return (
    <Modal
      id="edit-user-modal"
      isOpened={isOpened}
      onClose={handleCloseModal}
      width={510}
      disableBackdropClick
    >
      <ModalHeaders
        selected={selected}
        handleCloseModal={handleCloseModal}
        partner={partner}
      />
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        validateOnMount={true}
      >
        {({ values, touched, handleBlur, handleChange, setFieldValue }) => (
          <>
            <ModalBody>
              {selected ? (
                <DataSourcesForm
                  values={values}
                  touched={touched}
                  handleBlur={handleBlur}
                  handleChange={handleChange}
                  handleChangeWithError={handleChangeWithError}
                  setFieldValue={setFieldValue}
                  defaultSchema={defaultSchema}
                />
              ) : (
                <DataSourcesSelect
                  partner={partner}
                  setPartner={setPartner}
                  hoveredPartner={hoveredPartner}
                  setHoveredPartner={setHoveredPartner}
                  datas={dataSource}
                />
              )}
            </ModalBody>
            {error && (
              <ErrorContainer
                primaryText={
                  error === 400
                    ? CREDENTIAL_ERROR_MESSAGE
                    : GENERIC_REQUEST_ERROR_MESSAGE_PRIMARY
                }
                secondaryText={
                  error === 400 ? null : GENERIC_REQUEST_ERROR_MESSAGE_SECONDARY
                }
              />
            )}
            <ModalFooter justify="flex-end">
              <ButtonContainer horizontal>
                <Button
                  btnType="tertiary"
                  onClick={handleCloseModal}
                  size="large"
                >
                  Cancel
                </Button>
                <Button
                  size="large"
                  btnType="primary"
                  form="addUserModal"
                  mr={0}
                  onClick={() => handleSubmit(values)}
                  disabled={checkBtnDisabled(values)}
                >
                  {selected ? 'Submit' : 'Select Data Source'}
                </Button>
              </ButtonContainer>
            </ModalFooter>
          </>
        )}
      </Formik>
    </Modal>
  );
};

export default AddCredentialModal;
